import { FC, useEffect, useState, useRef } from 'react'
import { useGameStore } from '@/store'

interface PersistentPortfolioBarProps {
  onOpenPortfolio?: () => void
  onOpenTerminal?: () => void
}

export const PersistentPortfolioBar: FC<PersistentPortfolioBarProps> = ({
  onOpenPortfolio,
  onOpenTerminal,
}) => {
  const { session } = useGameStore()
  const [isFlashing, setIsFlashing] = useState(false)
  const [priceDirection, setPriceDirection] = useState<'up' | 'down' | null>(null)
  const prevValueRef = useRef<number | null>(null)

  if (!session) return null

  const { player, goal } = session
  const portfolioValue = player.portfolio.totalValue
  const cashBalance = player.portfolio.cashBalance
  const progressPercent = (portfolioValue / goal.targetAmount) * 100
  const daysRemaining = goal.daysRemaining

  // Detect value changes and flash
  useEffect(() => {
    if (prevValueRef.current !== null && prevValueRef.current !== portfolioValue) {
      const direction = portfolioValue > prevValueRef.current ? 'up' : 'down'
      setPriceDirection(direction)
      setIsFlashing(true)
      const timer = setTimeout(() => {
        setIsFlashing(false)
        setPriceDirection(null)
      }, 1000)
      return () => clearTimeout(timer)
    }
    prevValueRef.current = portfolioValue
  }, [portfolioValue])

  // Deadline warning colors
  const getDeadlineColor = () => {
    if (daysRemaining <= 3) return 'text-game-loss animate-pulse'
    if (daysRemaining <= 7) return 'text-game-loss'
    if (daysRemaining <= 14) return 'text-yellow-400'
    return 'text-game-muted'
  }

  // Progress bar color
  const getProgressColor = () => {
    if (progressPercent >= 100) return 'bg-game-profit'
    if (progressPercent >= 75) return 'bg-yellow-400'
    if (progressPercent >= 50) return 'bg-game-accent'
    return 'bg-game-accent/70'
  }

  // Value flash color
  const getValueColor = () => {
    if (isFlashing && priceDirection === 'up') return 'text-game-profit'
    if (isFlashing && priceDirection === 'down') return 'text-game-loss'
    return 'text-game-text'
  }

  return (
    <div className="bg-game-panel border-b-2 border-game-border px-2 py-1.5 flex items-center justify-between gap-2 flex-wrap">
      {/* Portfolio Value */}
      <button
        onClick={onOpenPortfolio}
        className="flex items-center gap-2 hover:bg-game-accent/10 px-2 py-0.5 -mx-2 transition-colors"
        title="Open Portfolio"
      >
        <span className="font-pixel text-xs text-game-muted">NET:</span>
        <span className={`font-mono text-sm font-bold transition-colors ${getValueColor()} ${isFlashing ? 'scale-105' : ''}`}>
          ${portfolioValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
        {priceDirection && (
          <span className={`text-xs ${priceDirection === 'up' ? 'text-game-profit' : 'text-game-loss'}`}>
            {priceDirection === 'up' ? '▲' : '▼'}
          </span>
        )}
      </button>

      {/* Cash Balance */}
      <button
        onClick={onOpenTerminal}
        className="flex items-center gap-2 hover:bg-game-accent/10 px-2 py-0.5 transition-colors"
        title="Open Terminal"
      >
        <span className="font-pixel text-xs text-game-muted">CASH:</span>
        <span className="font-mono text-sm text-game-profit">
          ${cashBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </button>

      {/* Goal Progress */}
      <div className="flex items-center gap-2 flex-1 min-w-[120px] max-w-[200px]">
        <span className="font-pixel text-xs text-game-muted">GOAL:</span>
        <div className="flex-1 h-3 bg-game-bg border border-game-border relative overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
          <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-white mix-blend-difference">
            {progressPercent.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Days Remaining */}
      <div className="flex items-center gap-1">
        <span className="font-pixel text-xs text-game-muted">DAYS:</span>
        <span className={`font-mono text-sm font-bold ${getDeadlineColor()}`}>
          {daysRemaining}
        </span>
        {daysRemaining <= 7 && (
          <span className="text-xs">⚠️</span>
        )}
      </div>

      {/* Target Amount (collapsed on small screens) */}
      <div className="hidden sm:flex items-center gap-1">
        <span className="font-pixel text-xs text-game-muted">TARGET:</span>
        <span className="font-mono text-xs text-game-gold">
          ${goal.targetAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>
    </div>
  )
}
